import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, ChevronDown, Globe, Shield } from "lucide-react";
import { useI18n, LANGS } from "../../i18n";
import { LOGO_URL } from "../../lib/api";
import { Container } from "../ui-kit";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "../ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";

const NAV = [
  { key: "nav_about", to: "/about", children: [["Our Story", "/about#story"], ["Our Mission", "/about#mission"], ["Governance", "/about#governance"], ["Transparency", "/transparency"]] },
  { key: "nav_programs", to: "/programs", children: [["Education", "/programs/education"], ["Healthcare", "/programs/healthcare"], ["Food Security", "/programs/food-security"], ["Water & Sanitation", "/programs/water-sanitation"], ["Community Development", "/programs/community-development"], ["Digital Inclusion", "/programs/digital-inclusion"]] },
  { key: "nav_impact", to: "/impact", children: [["Impact Dashboard", "/impact"], ["Our Reach", "/impact#reach"], ["Stories of Change", "/impact#stories"], ["Impact Reports", "/impact#reports"]] },
  { key: "nav_islamic", to: "/islamic-giving", children: [["Aayat Audio Library", "/aayat"], ["Zakat Calculator", "/zakat-calculator"], ["Zakat Guide", "/islamic-giving#guide"], ["Nisab Information", "/islamic-giving#nisab"], ["Sadaqah", "/islamic-giving#sadaqah"]] },
  { key: "nav_csr", to: "/csr", children: [["Corporate CSR", "/csr#corporate"], ["Employee Engagement", "/csr#employee"], ["Corporate Volunteering", "/csr#volunteering"], ["Become a CSR Partner", "/csr#partner"]] },
  { key: "nav_technology", to: "/technology" },
  { key: "nav_media", label: "Media", to: "/media" },
];

function LanguageSwitcher({ overHero }) {
  const { lang, setLang } = useI18n();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="language-switcher"
          className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition ${overHero ? "border-white/30 text-white hover:border-gold-light hover:text-gold-light" : "border-emerald-900/15 text-emerald-900 hover:border-gold hover:text-gold"}`}
        >
          <Globe className="h-4 w-4" />
          {LANGS.find((l) => l.code === lang)?.label}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white">
        {LANGS.map((l) => (
          <DropdownMenuItem key={l.code} data-testid={`lang-option-${l.code}`} onClick={() => setLang(l.code)} className={`cursor-pointer ${lang === l.code ? "font-bold text-emerald-800" : ""}`}>
            {l.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Header() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(!isHome);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    if (isHome) { onScroll(); window.addEventListener("scroll", onScroll, { passive: true }); }
    else setScrolled(true);
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  const overHero = isHome && !scrolled;
  const positionCls = isHome ? "fixed left-0 right-0 top-0" : "sticky top-0";
  const headerCls = overHero
    ? "border-transparent bg-transparent"
    : "border-b border-emerald-900/10 glass-nav";
  const linkCls = overHero
    ? "text-white/85 hover:bg-white/10 hover:text-white"
    : "text-emerald-900/80 hover:bg-emerald-50 hover:text-emerald-900";

  return (
    <header className={`${positionCls} z-50 transition-colors duration-500 ${headerCls}`} data-testid="site-header">
      <Container className={`flex items-center justify-between gap-2 transition-all duration-500 xl:px-6 ${overHero ? "h-20 sm:h-24" : "h-16 sm:h-20"}`}>
        <Link to="/" data-testid="logo-link" className="flex min-w-0 shrink items-center gap-2.5 sm:min-w-fit sm:shrink-0 sm:gap-3">
          <img src={LOGO_URL} alt="Helping Hands Dubai logo" className={`shrink-0 rounded-2xl bg-white object-contain p-1 shadow-md ring-2 ring-gold/50 transition-all duration-500 ${overHero ? "h-11 w-11 sm:h-16 sm:w-16" : "h-10 w-10 sm:h-14 sm:w-14"}`} />
          <div className="min-w-0 leading-tight sm:min-w-fit">
            <div className={`truncate font-display text-lg font-bold tracking-tight transition-colors duration-500 sm:overflow-visible sm:text-2xl ${overHero ? "text-white" : "text-emerald-900"}`}>Helping Hands</div>
            <div className="truncate text-[9px] font-bold uppercase tracking-[0.2em] text-gold sm:overflow-visible sm:text-[11px] sm:tracking-[0.28em]">Dubai · Global Solutions</div>
          </div>
        </Link>

        <nav className="hidden flex-nowrap items-center gap-0 whitespace-nowrap xl:flex">
          {NAV.map((item) => (
            <div key={item.key} className="group relative">
              <Link to={item.to} data-testid={`nav-${item.key}`} className={`flex items-center gap-0.5 whitespace-nowrap rounded-lg px-2 py-2 text-[13px] font-semibold transition ${linkCls}`}>
                {item.label || t(item.key)}
                {item.children && <ChevronDown className="h-3.5 w-3.5 opacity-60" />}
              </Link>
              {item.children && (
                <div className="invisible absolute start-0 top-full w-64 translate-y-2 rounded-2xl border border-emerald-900/10 bg-white p-2 opacity-0 shadow-xl transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                  {item.children.map(([label, to]) => (
                    <Link key={label} to={to} className="block rounded-lg px-3 py-2 text-sm text-slate-600 transition hover:bg-emerald-50 hover:text-emerald-900">{label}</Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-2">
          <LanguageSwitcher overHero={overHero} />
          <Link to="/programs" data-testid="header-support-cta" className="hidden whitespace-nowrap rounded-full bg-gold px-5 py-2.5 text-sm font-bold uppercase tracking-wider text-white transition hover:bg-gold-light xl:inline-flex">
            Support
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" className={`rounded-lg p-2 xl:hidden ${overHero ? "text-white" : "text-emerald-900"}`}>
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] max-w-sm overflow-y-auto bg-white">
              <SheetHeader>
                <SheetTitle className="font-display text-2xl text-emerald-900">Helping Hands</SheetTitle>
              </SheetHeader>
              <div className="mt-6 flex flex-col gap-1">
                {NAV.map((item) => (
                  <div key={item.key}>
                    <Link to={item.to} onClick={() => setOpen(false)} className="block rounded-lg px-3 py-2.5 text-base font-semibold text-emerald-900">{item.label || t(item.key)}</Link>
                    {item.children && (
                      <div className="ms-3 border-s border-emerald-900/10 ps-3">
                        {item.children.map(([label, to]) => (
                          <Link key={label} to={to} onClick={() => setOpen(false)} className="block py-1.5 text-sm text-slate-500">{label}</Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <button onClick={() => { setOpen(false); navigate("/admin"); }} className="mt-3 flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-500">
                  <Shield className="h-4 w-4" /> {t("admin_login")}
                </button>
                <Link to="/programs" onClick={() => setOpen(false)} className="mt-3 rounded-full bg-gold px-5 py-3 text-center font-bold text-white">{t("cta_support")}</Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </Container>
    </header>
  );
}
